import axios from 'axios';
import { API_BASE_URL } from '@/modules/shared/constants';
import { unwrapEnvelope, type ApiEnvelope } from '../shared/api';
import type { PaginatedEnvelope, InboxRow } from '../shared/types';

interface InboxListParams {
  limit?: number;
  offset: number;
  is_unread?: boolean;
  notification_type?: string;
  subcategory?: string;
  tags?: string;
}

export const fetchInbox = async (params: InboxListParams): Promise<PaginatedEnvelope<InboxRow>> => {
  const response = await axios.get<ApiEnvelope<PaginatedEnvelope<InboxRow>>>(
    `${API_BASE_URL}/communicator/notifications/inbox`,
    { params: { ...params, notification_type: 'direct' } },
  );
  const data = unwrapEnvelope(response.data);
  return data ?? { results: [], count: 0 };
};

// Bell-badge / count-only callers go through this helper. Always explicit
// about which slice (`is_unread=true` by default = unread tally).
export const fetchInboxCount = async (
  opts: { is_unread?: boolean; subcategory?: string; tags?: string } = {},
): Promise<number> => {
  const response = await axios.get<ApiEnvelope<{ count: number }>>(`${API_BASE_URL}/communicator/notifications/inbox`, {
    params: {
      get_count: true,
      is_unread: opts.is_unread ?? true,
      notification_type: 'direct',
      ...(opts.subcategory ? { subcategory: opts.subcategory } : {}),
      ...(opts.tags ? { tags: opts.tags } : {}),
    },
  });
  return unwrapEnvelope(response.data)?.count ?? 0;
};

// Per-subcategory counts in a single round-trip. Backend rolls NULL up
// under the `general` key (matching `subcategory || "general"` on the FE).
export interface InboxCountsByCategory {
  counts: Record<string, number>;
  total: number;
}

export const fetchInboxCountsByCategory = async (
  opts: { is_unread?: boolean } = {},
): Promise<InboxCountsByCategory> => {
  const response = await axios.get<ApiEnvelope<InboxCountsByCategory>>(
    `${API_BASE_URL}/communicator/notifications/inbox`,
    {
      params: {
        get_count: true,
        group_by: 'subcategory',
        is_unread: opts.is_unread ?? true,
        notification_type: 'direct',
      },
    },
  );
  return unwrapEnvelope(response.data) ?? { counts: {}, total: 0 };
};
